// Static architecture diagram for the AI harness.
//
// Deliberately NOT a live demo: the claim this card makes is architectural — where
// the determinism boundary sits and what the model is allowed to touch — and that is
// a structural claim, better shown as structure than as output. No loader, no model
// calls, no charting dependency.
//
// ── Why the coordinates below are hand-authored ────────────────────────────────
// This component is in the MAIN client bundle, not a /harness chunk:
// features/writing/articles.ts globs /content/writing/*.mdx with `eager: true`, and
// types-of-agentic-harness.mdx imports this file. So any layout library it pulled in
// would ship to every visitor, homepage included. Layout is therefore precomputed
// and the runtime does no measuring — no ResizeObserver, no JS layout, zero deps.
//
// @dagrejs/dagre WAS evaluated for generating these tables (2026-08-03, via npx —
// never a project dep). It flattens the graph: with rankdir 'LR' all six chain nodes
// land in six sequential columns on one row, and `rules` is displaced vertically only
// as a crossing-reduction artifact of sharing a rank with `propose` — not as a lane.
// The model-above / engine-below split and the determinism boundary are semantic
// constraints, not things a graph-drawing algorithm optimises for, so there is no
// generator script to re-run. The tables here are the source of truth.
//
// IF YOU ADD OR RENAME A NODE you must hand-edit all three tables, re-wrap the text
// (SVG has no text wrapping — `title`/`sub` are pre-split into lines sized to each
// layout's node width), and re-check the edge paths, which reference anchor points by
// literal value.
//
// ── Why three layouts rather than one that scales ─────────────────────────────
// The diagram may grow TALLER but must never be WIDER than its container. Scaling one
// 672-wide canvas down to a 375px phone would land body text near 5px, so each
// breakpoint is a genuine reflow with its own viewBox and aspect ratio, selected with
// CSS. Widths are chosen so text renders at ~native size where it matters: the article
// (`max-w-2xl`, 672px) is the narrowest desktop mount, hence the 672 desktop viewBox.
//
// Edge colours are CSS custom properties (--flow-* in styles/tailwind.css, defined for
// both :root and .dark) so the diagram follows the theme toggle rather than baking in
// hex.

type EdgeKind = 'feed' | 'propose' | 'commit' | 'rebuild'

const EDGE_COLOR: Record<EdgeKind, string> = {
	feed: 'var(--flow-muted)',
	propose: 'var(--flow-propose)',
	commit: 'var(--flow-commit)',
	rebuild: 'var(--flow-rebuild)',
}

const DASHED: Record<EdgeKind, boolean> = {
	feed: false,
	propose: false,
	commit: false,
	rebuild: true,
}

const LEGEND: { kind: EdgeKind; label: string }[] = [
	{ kind: 'feed', label: 'input' },
	{ kind: 'propose', label: 'proposal' },
	{ kind: 'commit', label: 'passes both gates' },
	{ kind: 'rebuild', label: 'fails a gate → rebuild' },
]

// Text metrics. SVG cannot wrap, so `title`/`sub` below are pre-split; these sizes are
// what the line breaks were measured against.
const TITLE_SIZE = 12.5
const TITLE_LEAD = 15
const SUB_SIZE = 10.5
const SUB_LEAD = 13.5
const PAD = 12

type DNode = {
	id: string
	x: number
	y: number
	w: number
	h: number
	title: string[]
	sub: string[]
	commit?: boolean
}

type DEdge = { id: string; d: string; kind: EdgeKind }

type Band = { label: string; x: number; y: number; w: number; h: number }

type Layout = {
	id: string
	className: string
	vw: number
	vh: number
	bands: Band[]
	boundaryY: number
	boundaryLabel: { x: number; y: number }
	loopLabel: { x: number; y: number; vertical?: boolean }
	nodes: DNode[]
	edges: DEdge[]
}

const BOUNDARY_TEXT = 'the model never writes to the ledger'
const LOOP_TEXT = 'deleted and rebuilt, not patched'

const LANE_MODEL = 'Non-deterministic — the model'
const LANE_ENGINE = 'Deterministic — the engine'

// ── Desktop: two horizontal lanes, 3 columns above / 4 below. 672 wide so the
// article mount (max-w-2xl) renders it at 1:1 and the /harness route (max-w-3xl)
// scales it up slightly rather than down.
const DESKTOP: Layout = {
	id: 'desktop',
	className: 'hidden md:block',
	vw: 672,
	vh: 356,
	bands: [
		{ label: LANE_MODEL, x: 4, y: 10, w: 664, h: 126 },
		{ label: LANE_ENGINE, x: 4, y: 186, w: 664, h: 124 },
	],
	boundaryY: 161,
	boundaryLabel: { x: 170, y: 161 },
	loopLabel: { x: 336, y: 332 },
	nodes: [
		// Top lane — non-deterministic, the model. Inner width 176.
		{
			id: 'evidence',
			x: 12,
			y: 36,
			w: 200,
			h: 88,
			title: ['Messy evidence'],
			sub: ['statements, PDFs, screen', 'recordings, on-chain events'],
		},
		{
			id: 'parse',
			x: 236,
			y: 36,
			w: 200,
			h: 88,
			title: ['Multi-modal parse'],
			sub: ['Whisper · Qwen3-VL · OCR —', 'local models, end to end'],
		},
		{
			id: 'propose',
			x: 460,
			y: 36,
			w: 200,
			h: 88,
			title: ['Model proposes'],
			sub: [
				'classified transactions,',
				'matched sources, extracted',
				'entries',
			],
		},
		// Bottom lane — deterministic, the engine. Inner width 123.
		{
			id: 'rules',
			x: 12,
			y: 210,
			w: 147,
			h: 88,
			title: ['Rules + raw events'],
			sub: ['the immutable inputs'],
		},
		{
			id: 'regen',
			x: 179,
			y: 210,
			w: 147,
			h: 88,
			title: ['Regenerate ledger'],
			sub: ['derived from inputs,', 'never hand-edited'],
		},
		{
			id: 'gates',
			x: 346,
			y: 210,
			w: 147,
			h: 88,
			title: ['Gates'],
			sub: [
				'double-entry balances ·',
				'every figure traces to',
				'an event and a rule',
			],
		},
		{
			id: 'ledger',
			x: 513,
			y: 210,
			w: 147,
			h: 88,
			title: ['Committed ledger'],
			sub: ['audit-grade by', 'construction'],
			commit: true,
		},
	],
	edges: [
		{ id: 'evidence-parse', d: 'M 212 80 L 232 80', kind: 'feed' },
		{ id: 'parse-propose', d: 'M 436 80 L 456 80', kind: 'feed' },
		// The crossing: proposals leave the model lane and enter the engine lane.
		{
			id: 'propose-regen',
			d: 'M 560 124 C 545 176, 330 158, 252.5 204',
			kind: 'propose',
		},
		{ id: 'rules-regen', d: 'M 159 254 L 175 254', kind: 'feed' },
		{ id: 'regen-gates', d: 'M 326 254 L 342 254', kind: 'feed' },
		{ id: 'gates-ledger', d: 'M 493 254 L 509 254', kind: 'commit' },
		// The loop that carries the whole thesis — a failed proposal is not patched.
		{
			id: 'rebuild',
			d: 'M 419.5 298 L 419.5 332 L 252.5 332 L 252.5 302',
			kind: 'rebuild',
		},
	],
}

// ── Tablet: lanes kept, but each wraps to two columns. The engine lane runs
// boustrophedon (rules → regen → gates → ledger, right-to-left on the second row) so
// the rebuild return is a short vertical rather than a long path back over the lane.
const TABLET: Layout = {
	id: 'tablet',
	className: 'hidden sm:block md:hidden',
	vw: 620,
	vh: 452,
	bands: [
		{ label: LANE_MODEL, x: 4, y: 10, w: 612, h: 184 },
		{ label: LANE_ENGINE, x: 4, y: 238, w: 612, h: 208 },
	],
	boundaryY: 214,
	boundaryLabel: { x: 170, y: 214 },
	// In the row gap, on the loop itself — clear of the regen→gates vertical at x=350
	// and the rebuild vertical at x=570.
	loopLabel: { x: 460, y: 342 },
	nodes: [
		// Inner width 240, except `propose` which spans the lane (inner 524).
		{
			id: 'evidence',
			x: 44,
			y: 36,
			w: 264,
			h: 74,
			title: ['Messy evidence'],
			sub: ['statements, PDFs, screen recordings,', 'on-chain events'],
		},
		{
			id: 'parse',
			x: 328,
			y: 36,
			w: 264,
			h: 74,
			title: ['Multi-modal parse'],
			sub: ['Whisper · Qwen3-VL · OCR — local', 'models, end to end'],
		},
		{
			id: 'propose',
			x: 44,
			y: 122,
			w: 548,
			h: 60,
			title: ['Model proposes'],
			sub: ['classified transactions, matched sources, extracted entries'],
		},
		{
			id: 'rules',
			x: 44,
			y: 264,
			w: 264,
			h: 60,
			title: ['Rules + raw events'],
			sub: ['the immutable inputs'],
		},
		{
			id: 'regen',
			x: 328,
			y: 264,
			w: 264,
			h: 60,
			title: ['Regenerate ledger'],
			sub: ['derived from inputs, never hand-edited'],
		},
		{
			id: 'gates',
			x: 328,
			y: 360,
			w: 264,
			h: 74,
			title: ['Gates'],
			sub: [
				'double-entry balances · every figure',
				'traces to an event and a rule',
			],
		},
		{
			id: 'ledger',
			x: 44,
			y: 360,
			w: 264,
			h: 74,
			title: ['Committed ledger'],
			sub: ['audit-grade by construction'],
			commit: true,
		},
	],
	edges: [
		{ id: 'evidence-parse', d: 'M 308 73 L 324 73', kind: 'feed' },
		{ id: 'parse-propose', d: 'M 460 110 L 460 120', kind: 'feed' },
		{
			id: 'propose-regen',
			d: 'M 318 182 C 318 216, 460 220, 460 260',
			kind: 'propose',
		},
		{ id: 'rules-regen', d: 'M 308 294 L 324 294', kind: 'feed' },
		{ id: 'regen-gates', d: 'M 350 324 L 350 356', kind: 'feed' },
		{ id: 'gates-ledger', d: 'M 328 397 L 312 397', kind: 'commit' },
		{ id: 'rebuild', d: 'M 570 360 L 570 328', kind: 'rebuild' },
	],
}

// ── Mobile: single column, lanes stacked. The crossing runs down the right gutter,
// the rebuild return down the left with a rotated label — a vertical reflow, not a
// shrunken desktop.
const MOBILE: Layout = {
	id: 'mobile',
	className: 'sm:hidden',
	// 320 wide so a 375px phone (≈311px of content) renders it at ~0.97 rather than
	// shrinking the type.
	vw: 320,
	vh: 710,
	bands: [
		{ label: LANE_MODEL, x: 4, y: 10, w: 312, h: 296 },
		{ label: LANE_ENGINE, x: 4, y: 350, w: 312, h: 346 },
	],
	boundaryY: 326,
	boundaryLabel: { x: 160, y: 326 },
	// The rotated pill occupies x 4–24, so the rebuild edge runs at x=30 to stay
	// clear of it — at x=24 the label rect masks the whole vertical run.
	loopLabel: { x: 14, y: 526, vertical: true },
	nodes: [
		// Inner width 234.
		{
			id: 'evidence',
			x: 40,
			y: 36,
			w: 258,
			h: 74,
			title: ['Messy evidence'],
			sub: ['statements, PDFs, screen recordings,', 'on-chain events'],
		},
		{
			id: 'parse',
			x: 40,
			y: 128,
			w: 258,
			h: 74,
			title: ['Multi-modal parse'],
			sub: ['Whisper · Qwen3-VL · OCR — local', 'models, end to end'],
		},
		{
			id: 'propose',
			x: 40,
			y: 220,
			w: 258,
			h: 74,
			title: ['Model proposes'],
			sub: ['classified transactions, matched', 'sources, extracted entries'],
		},
		{
			id: 'rules',
			x: 40,
			y: 376,
			w: 258,
			h: 60,
			title: ['Rules + raw events'],
			sub: ['the immutable inputs'],
		},
		{
			id: 'regen',
			x: 40,
			y: 454,
			w: 258,
			h: 60,
			title: ['Regenerate ledger'],
			sub: ['derived from inputs, never hand-edited'],
		},
		{
			id: 'gates',
			x: 40,
			y: 532,
			w: 258,
			h: 74,
			title: ['Gates'],
			sub: [
				'double-entry balances · every figure',
				'traces to an event and a rule',
			],
		},
		{
			id: 'ledger',
			x: 40,
			y: 624,
			w: 258,
			h: 60,
			title: ['Committed ledger'],
			sub: ['audit-grade by construction'],
			commit: true,
		},
	],
	edges: [
		{ id: 'evidence-parse', d: 'M 169 110 L 169 124', kind: 'feed' },
		{ id: 'parse-propose', d: 'M 169 202 L 169 216', kind: 'feed' },
		{
			id: 'propose-regen',
			d: 'M 298 257 L 310 257 L 310 484 L 302 484',
			kind: 'propose',
		},
		{ id: 'rules-regen', d: 'M 169 436 L 169 450', kind: 'feed' },
		{ id: 'regen-gates', d: 'M 169 514 L 169 528', kind: 'feed' },
		{ id: 'gates-ledger', d: 'M 169 606 L 169 620', kind: 'commit' },
		{
			id: 'rebuild',
			d: 'M 40 569 L 30 569 L 30 484 L 36 484',
			kind: 'rebuild',
		},
	],
}

const LAYOUTS = [DESKTOP, TABLET, MOBILE]

// A pill drawn in SVG — rect sized from the text so it masks the line it sits on.
function Pill({
	text,
	x,
	y,
	stroke,
	vertical,
}: {
	text: string
	x: number
	y: number
	stroke: string
	vertical?: boolean
}) {
	const w = text.length * 5.4 + 16
	const h = 20
	const body = (
		<>
			<rect
				x={x - w / 2}
				y={y - h / 2}
				width={w}
				height={h}
				rx={4}
				fill="var(--color-card)"
				stroke={stroke}
				strokeWidth={1}
			/>
			<text
				x={x}
				y={y}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={SUB_SIZE}
				fill="var(--color-muted-foreground)"
			>
				{text}
			</text>
		</>
	)
	return vertical ? (
		<g transform={`rotate(-90 ${x} ${y})`}>{body}</g>
	) : (
		<g>{body}</g>
	)
}

function DiagramSvg({ layout }: { layout: Layout }) {
	const { vw, vh } = layout
	return (
		<svg
			viewBox={`0 0 ${vw} ${vh}`}
			preserveAspectRatio="xMidYMid meet"
			role="img"
			aria-label="The model proposes over messy evidence; a deterministic engine regenerates the ledger from rules and raw events, and a proposal that fails a gate is discarded and rebuilt rather than patched."
			style={{ width: '100%', height: 'auto', display: 'block' }}
		>
			<defs>
				{(Object.keys(EDGE_COLOR) as EdgeKind[]).map((k) => (
					<marker
						key={k}
						id={`harness-${layout.id}-arrow-${k}`}
						markerWidth="9"
						markerHeight="7"
						refX="8.5"
						refY="3.5"
						orient="auto"
						// userSpaceOnUse, not the strokeWidth default — otherwise the head
						// is scaled by strokeWidth 2 and renders as a 20px blob.
						markerUnits="userSpaceOnUse"
					>
						<path d="M0,0 L9,3.5 L0,7 z" fill={EDGE_COLOR[k]} />
					</marker>
				))}
			</defs>

			{/* Lane bands — the architecture claim is the split itself. */}
			{layout.bands.map((b) => (
				<g key={b.label}>
					<rect
						x={b.x}
						y={b.y}
						width={b.w}
						height={b.h}
						rx={8}
						fill="var(--color-muted)"
						fillOpacity={0.3}
						stroke="var(--color-border)"
						strokeOpacity={0.7}
						strokeWidth={1}
						strokeDasharray="4 4"
					/>
					<text
						x={b.x + 14}
						y={b.y + 18}
						fontSize={10.5}
						fontWeight={500}
						letterSpacing="0.06em"
						fill="var(--color-muted-foreground)"
						style={{ textTransform: 'uppercase' }}
					>
						{b.label.toUpperCase()}
					</text>
				</g>
			))}

			{/* The determinism boundary. */}
			<line
				x1={8}
				y1={layout.boundaryY}
				x2={vw - 8}
				y2={layout.boundaryY}
				stroke="var(--flow-muted)"
				strokeWidth={1.5}
				strokeDasharray="2 6"
			/>

			{/* Edges sit under the nodes. */}
			{layout.edges.map((e) => (
				<path
					key={e.id}
					d={e.d}
					fill="none"
					stroke={EDGE_COLOR[e.kind]}
					strokeWidth={2}
					strokeDasharray={DASHED[e.kind] ? '6 4' : undefined}
					markerEnd={`url(#harness-${layout.id}-arrow-${e.kind})`}
				/>
			))}

			{layout.nodes.map((n) => {
				const titleTop = n.y + PAD + TITLE_SIZE * 0.88
				const subTop = titleTop + (n.title.length - 1) * TITLE_LEAD + 17
				return (
					<g key={n.id}>
						<rect
							x={n.x}
							y={n.y}
							width={n.w}
							height={n.h}
							rx={8}
							fill="var(--color-card)"
							stroke={n.commit ? EDGE_COLOR.commit : 'var(--color-border)'}
							strokeWidth={2}
						/>
						{n.title.map((line, i) => (
							<text
								key={line}
								x={n.x + PAD}
								y={titleTop + i * TITLE_LEAD}
								fontSize={TITLE_SIZE}
								fontWeight={600}
								fill="var(--color-foreground)"
							>
								{line}
							</text>
						))}
						{n.sub.map((line, i) => (
							<text
								key={line}
								x={n.x + PAD}
								y={subTop + i * SUB_LEAD}
								fontSize={SUB_SIZE}
								fill="var(--color-muted-foreground)"
							>
								{line}
							</text>
						))}
					</g>
				)
			})}

			{/* Labels float above both edges and nodes. */}
			<Pill
				text={BOUNDARY_TEXT}
				x={layout.boundaryLabel.x}
				y={layout.boundaryLabel.y}
				stroke="var(--color-border)"
			/>
			<Pill
				text={LOOP_TEXT}
				x={layout.loopLabel.x}
				y={layout.loopLabel.y}
				stroke={EDGE_COLOR.rebuild}
				vertical={layout.loopLabel.vertical}
			/>
		</svg>
	)
}

export function HarnessDiagram() {
	return (
		<figure className="not-prose my-8">
			<div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
				{LEGEND.map((l) => (
					<span key={l.kind} className="flex items-center gap-1.5">
						<span
							className="inline-block h-0 w-5 border-t-2"
							style={{
								borderColor: EDGE_COLOR[l.kind],
								borderStyle: DASHED[l.kind] ? 'dashed' : 'solid',
							}}
						/>
						<span className="text-muted-foreground">{l.label}</span>
					</span>
				))}
			</div>

			{LAYOUTS.map((l) => (
				<div key={l.id} className={l.className}>
					<DiagramSvg layout={l} />
				</div>
			))}

			<figcaption className="text-muted-foreground mt-4 text-sm text-pretty">
				The model proposes; the deterministic engine decides. A proposal that
				fails either gate is discarded and the ledger regenerated from inputs
				and rules — never patched in place. That is how non-deterministic
				reasoning coexists with books that have to survive an audit. The same
				harness pattern, pointed at web development instead of accounting, built
				this site.
			</figcaption>
		</figure>
	)
}
